import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { CheckCircle, XCircle, Loader2, ArrowLeft, Heart } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function DonationSuccessPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('checking');
  const [data, setData] = useState(null);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }

    const poll = async (attempt) => {
      if (attempt >= 5) {
        setStatus('timeout');
        return;
      }
      try {
        const res = await api.get(`/donations/status/${sessionId}`);
        setData(res.data);
        setAttempts(attempt + 1);
        if (res.data.payment_status === 'paid') {
          setStatus('success');
          return;
        } else if (res.data.status === 'expired') {
          setStatus('expired');
          return;
        }
        setStatus('processing');
        setTimeout(() => poll(attempt + 1), 2000);
      } catch {
        if (attempt < 4) {
          setTimeout(() => poll(attempt + 1), 2000);
        } else {
          setStatus('error');
        }
      }
    };

    poll(0);
  }, [sessionId]);

  const formatAmount = (cents) => {
    if (!cents) return '$0.00';
    return `$${(cents / 100).toFixed(2)}`;
  };

  return (
    <div data-testid="donation-success-page" className="pt-20 min-h-screen bg-[#F8FAFC]">
      <div className="max-w-lg mx-auto px-6 py-16 sm:py-24">
        {status === 'checking' || status === 'processing' ? (
          <div data-testid="donation-processing" className="bg-white rounded-2xl border border-[#E2E8F0] shadow-lg p-6 text-center sm:p-10">
            <Loader2 size={48} className="mx-auto text-[#1E4FA1] animate-spin mb-4" />
            <h2 className="text-2xl font-bold text-[#0F172A] mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Processing Your Donation
            </h2>
            <p className="text-[#94A3B8]">Please wait while we confirm your payment...</p>
            <p className="text-xs text-[#94A3B8] mt-2">Attempt {attempts + 1}/5</p>
          </div>
        ) : status === 'success' ? (
          <div data-testid="donation-success" className="bg-white rounded-2xl border border-[#E2E8F0] shadow-lg p-6 text-center sm:p-10">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle size={40} className="text-green-600" />
            </div>
            <h2 className="text-3xl font-bold text-[#0F172A] mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Thank You!
            </h2>
            <p className="text-[#475569] mb-4">Your generous donation has been received.</p>
            {data && (
              <div className="bg-[#EBF2FA] rounded-xl p-4 mb-6">
                <p className="text-[#1E4FA1] text-2xl font-bold">{formatAmount(data.amount_total)}</p>
                <p className="text-[#475569] text-sm mt-1">Donation Amount</p>
              </div>
            )}
            <p className="text-[#94A3B8] text-sm mb-6">
              Your contribution helps us continue shining the light of Christ among the nations. God bless you!
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/">
                <Button data-testid="back-home-btn" className="bg-[#1E4FA1] hover:bg-[#153A7A] text-white rounded-xl px-6">
                  <ArrowLeft size={16} className="mr-2" /> Back to Home
                </Button>
              </Link>
              <Link to="/donate">
                <Button data-testid="donate-again-btn" variant="outline" className="rounded-xl px-6">
                  <Heart size={16} className="mr-2" /> Donate Again
                </Button>
              </Link>
            </div>
          </div>
        ) : (
          <div data-testid="donation-failed" className="bg-white rounded-2xl border border-[#E2E8F0] shadow-lg p-6 text-center sm:p-10">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle size={40} className="text-red-500" />
            </div>
            <h2 className="text-2xl font-bold text-[#0F172A] mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              {status === 'timeout' ? 'Payment Status Pending' : status === 'expired' ? 'Session Expired' : 'Something Went Wrong'}
            </h2>
            <p className="text-[#94A3B8] mb-6">
              {status === 'timeout'
                ? 'We couldn\'t confirm your payment yet. If you were charged, it will be reflected shortly.'
                : status === 'expired'
                  ? 'The payment session has expired. Please try again.'
                  : 'There was an issue processing your donation. Please try again.'}
            </p>
            <Link to="/donate">
              <Button data-testid="try-again-btn" className="bg-[#1E4FA1] hover:bg-[#153A7A] text-white rounded-xl px-6">
                Try Again
              </Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
